/**
 * useTickerData - Custom Hook
 * 
 * Hook que maneja la obtención, caching y actualización de datos del ticker.
 * Realiza peticiones al endpoint de datos del ticker y proporciona estados de carga/error.
 * Incluye sistema de polling automático para mantener datos actualizados.
 * 
 * @hook
 * @param {Object} options - Opciones del hook
 * @param {number} options.pollInterval - Intervalo de polling en ms (default: 60000 = 60s)
 * @param {boolean} options.enablePolling - Activar polling automático (default: true)
 * @returns {Object} Objeto con propiedades:
 *   - tickerData {Array<TickerLeagueData>} Array de datos del ticker por liga
 *   - loading {boolean} true mientras se cargan los datos
 *   - error {string|null} Mensaje de error si algo falla
 *   - lastUpdate {Date|null} Fecha del último update
 *   - refetch {Function} Función para forzar recarga de datos
 * 
 * Estructura de datos:
 * tickerData = [
 *   {
 *     league: { id, name, sport, ... },
 *     standing: {...},        // Tabla de posiciones
 *     results: [{...}, ...],  // Últimos 10 partidos
 *     upcoming: [{...}, ...]  // Próximos 10 partidos
 *   },
 *   ...
 * ]
 * 
 * Comportamiento:
 * - Se ejecuta una sola vez al montar el componente
 * - Si enablePolling es true, actualiza automáticamente cada pollInterval ms
 * - El polling se pausa si el documento pierde foco
 * - El polling se reanuda cuando el documento recupera foco
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { getTickerData } from '../services/tickerData';

export const useTickerData = (options = {}) => {
  const {
    pollInterval = 60000,  // 60 segundos por defecto
    enablePolling = true
  } = options;

  const [tickerData, setTickerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const pollIntervalRef = useRef(null);
  const isVisibleRef = useRef(true);

  /**
   * Función para obtener datos del ticker
   */
  const fetchTickerData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getTickerData();

      if (response.statusCode === 200) {
        setTickerData(response.data);
        setLastUpdate(new Date());
      } else {
        setError(response.message || 'Error al obtener datos del ticker');
      }
    } catch (err) {
      console.error('Error fetching ticker data:', err);
      setError('Error al obtener datos del ticker');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Efecto para cargar datos inicial y configurar polling
   */
  useEffect(() => {
    // Cargar datos inicial
    fetchTickerData();

    // Configurar polling si está habilitado
    if (enablePolling) {
      // Detectar cambios de visibilidad del documento
      const handleVisibilityChange = () => {
        isVisibleRef.current = !document.hidden;

        if (isVisibleRef.current && enablePolling) {
          // Reanudar polling cuando vuelve a ser visible
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
          }
          pollIntervalRef.current = setInterval(fetchTickerData, pollInterval);
        } else if (!isVisibleRef.current && pollIntervalRef.current) {
          // Pausar polling cuando pierde foco
          clearInterval(pollIntervalRef.current);
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Iniciar polling
      pollIntervalRef.current = setInterval(fetchTickerData, pollInterval);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchTickerData, pollInterval, enablePolling]);

  /**
   * Función para forzar recarga de datos
   * Útil para acciones manuales del usuario
   */
  const refetch = useCallback(async () => {
    await fetchTickerData();
  }, [fetchTickerData]);

  return {
    tickerData,
    loading,
    error,
    lastUpdate,
    refetch
  };
};
